import React, { useState, useEffect } from 'react';
import { useGamification } from '../../../context/GamificationContext';
import { useAuth } from '../../../context/AuthContext';
import { useTeam } from '../../../context/TeamContext';
import { useTCoins } from '../../../context/TCoinsContext';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import NurseBottomNavigation from '../Layout/NurseBottomNavigation';
import { getMascotByXP, MASCOT_LEVELS } from '../../../data/nurseAppModels';
import { getAllBadges, getBadgesByType, BADGE_TYPES } from '../../../data/badgeDefinitions';
import { getAllAchievements } from '../../../data/achievementDefinitions';
import BadgeDisplay from './BadgeDisplay';
import AchievementDisplay from './AchievementDisplay';
import ChallengeDisplay from './ChallengeDisplay';
import LeaderboardDisplay from './LeaderboardDisplay';
import LevelUpModal from './LevelUpModal';

const GamificationDashboardPage = () => {
  const { currentUser } = useAuth();
  const { currentTeam } = useTeam();
  const { stats, badges, achievements, levelUpInfo, clearLevelUpInfo, loading: statsLoading } = useGamification();
  const { getTotalTCoins } = useTCoins();
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'badges', 'achievements', 'challenges', 'leaderboard'
  const [recentActivities, setRecentActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(true);

  // Load recent activities
  useEffect(() => {
    const loadRecentActivities = async () => {
      if (!currentUser?.uid) {
        setLoadingActivities(false);
        return;
      }

      try {
        const activitiesRef = collection(db, 'userActivities');
        const q = query(
          activitiesRef,
          where('userId', '==', currentUser.uid),
          orderBy('timestamp', 'desc'),
          limit(10)
        );
        const snapshot = await getDocs(q);
        const activities = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRecentActivities(activities);
      } catch (error) {
        console.error('Error loading recent activities:', error);
      } finally {
        setLoadingActivities(false);
      }
    };

    loadRecentActivities();
  }, [currentUser?.uid]);

  const getActivityIcon = (activityType) => {
    const icons = {
      CLOCK_IN: '⏰',
      CLOCK_OUT: '✅',
      SHIFT_COMPLETE: '📅',
      STOCK_IN: '📦',
      STOCK_OUT: '📤',
      INSPECTION: '🔍',
      BORROW_EQUIPMENT: '📥',
      RETURN_EQUIPMENT: '📤',
      REPORT_ISSUE: '⚠️',
      SEND_REPAIR_REQUEST: '🔧',
      COMPLETE_REPAIR: '✅',
      JOIN_TEAM: '👥',
      DAILY_LOGIN: '🎁',
      GIVE_GIFT: '🎁',
      RECEIVE_GIFT: '💝',
      LEVEL_UP: '⭐'
    };
    return icons[activityType] || '🎯';
  };

  const getActivityLabel = (activityType) => {
    const labels = {
      CLOCK_IN: 'เข้าเวร',
      CLOCK_OUT: 'ออกเวร',
      SHIFT_COMPLETE: 'เสร็จสิ้นเวร',
      STOCK_IN: 'รับ Stock',
      STOCK_OUT: 'จ่าย Stock',
      INSPECTION: 'ตรวจสอบอุปกรณ์',
      BORROW_EQUIPMENT: 'ยืมอุปกรณ์',
      RETURN_EQUIPMENT: 'คืนอุปกรณ์',
      REPORT_ISSUE: 'แจ้งปัญหา',
      SEND_REPAIR_REQUEST: 'ส่งซ่อม',
      COMPLETE_REPAIR: 'ซ่อมเสร็จ',
      JOIN_TEAM: 'เข้าร่วมทีม',
      DAILY_LOGIN: 'เข้าสู่ระบบ',
      GIVE_GIFT: 'ให้ของขวัญ',
      RECEIVE_GIFT: 'รับของขวัญ',
      LEVEL_UP: 'Level Up'
    };
    return labels[activityType] || activityType;
  };

  const getNextLevelXP = () => {
    if (!stats.mascot) return null;
    const currentLevel = stats.mascot.level;
    if (currentLevel >= 5) return null; // Max level
    const nextLevel = MASCOT_LEVELS[currentLevel + 1];
    return nextLevel?.minXP || null;
  };

  const getProgressToNextLevel = () => {
    if (!stats.mascot) return 0;
    const nextLevelXP = getNextLevelXP();
    if (!nextLevelXP) return 100;
    const currentXP = stats.currentXP;
    const currentLevelXP = stats.mascot.minXP;
    const progress = ((currentXP - currentLevelXP) / (nextLevelXP - currentLevelXP)) * 100;
    return Math.max(0, Math.min(100, progress));
  };

  if (statsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pb-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังโหลด...</p>
        </div>
        <NurseBottomNavigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">🎮 Gamification</h1>
          <p className="text-gray-600">ดูความก้าวหน้าและรางวัลของคุณ</p>
        </div>

        {/* Stats Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {/* Points Card */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">คะแนนรวม</p>
                <p className="text-2xl font-bold text-blue-600">{stats.totalPoints.toLocaleString()}</p>
              </div>
              <div className="text-4xl">⭐</div>
            </div>
          </div>

          {/* XP Card */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">XP ปัจจุบัน</p>
                <p className="text-2xl font-bold text-green-600">{stats.currentXP.toLocaleString()}</p>
              </div>
              <div className="text-4xl">💎</div>
            </div>
          </div>

          {/* Level Card */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Level</p>
                <p className="text-2xl font-bold text-purple-600">
                  {stats.mascot ? `${stats.mascot.level}: ${stats.mascot.nameThai}` : '1: มือใหม่'}
                </p>
              </div>
              <div className="text-4xl">👑</div>
            </div>
          </div>

          {/* T Coins Card */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">T Coins</p>
                <p className="text-2xl font-bold text-yellow-600">{getTotalTCoins().toLocaleString()}</p>
              </div>
              <div className="text-4xl">🪙</div>
            </div>
          </div>
        </div>

        {/* Level Progress */}
        {stats.mascot && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-800">
                ความคืบหน้า: Level {stats.mascot.level} - {stats.mascot.nameThai}
              </h3>
              {getNextLevelXP() && (
                <span className="text-sm text-gray-600">
                  ต้องการ {getNextLevelXP() - stats.currentXP} XP เพื่อ Level {stats.mascot.level + 1}
                </span>
              )}
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
              <div
                className="h-4 rounded-full transition-all duration-300 bg-gradient-to-r from-purple-500 to-pink-500"
                style={{ width: `${getProgressToNextLevel()}%` }}
              ></div>
            </div>
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>XP: {stats.currentXP.toLocaleString()}</span>
              {getNextLevelXP() && (
                <span>เป้าหมาย: {getNextLevelXP().toLocaleString()} XP</span>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto scrollbar-hide">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex-shrink-0 px-4 py-2 font-medium ${
              activeTab === 'overview'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            ภาพรวม
          </button>
          <button
            onClick={() => setActiveTab('badges')}
            className={`flex-shrink-0 px-4 py-2 font-medium ${
              activeTab === 'badges'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Badges
          </button>
          <button
            onClick={() => setActiveTab('achievements')}
            className={`flex-shrink-0 px-4 py-2 font-medium ${
              activeTab === 'achievements'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Achievements
          </button>
          <button
            onClick={() => setActiveTab('challenges')}
            className={`flex-shrink-0 px-4 py-2 font-medium ${
              activeTab === 'challenges'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Challenges
          </button>
          <button
            onClick={() => setActiveTab('leaderboard')}
            className={`flex-shrink-0 px-4 py-2 font-medium ${
              activeTab === 'leaderboard'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Leaderboard
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* User Stats */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">📊 สถิติการใช้งาน</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-3xl mb-2">📅</div>
                  <div className="text-2xl font-bold text-blue-600">{stats.stats.totalShifts || 0}</div>
                  <div className="text-sm text-gray-600">เวรที่ทำ</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl mb-2">🔍</div>
                  <div className="text-2xl font-bold text-green-600">{stats.stats.totalInspections || 0}</div>
                  <div className="text-sm text-gray-600">การตรวจสอบ</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl mb-2">📥</div>
                  <div className="text-2xl font-bold text-purple-600">{stats.stats.totalBorrows || 0}</div>
                  <div className="text-sm text-gray-600">การยืม</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl mb-2">🔧</div>
                  <div className="text-2xl font-bold text-orange-600">{stats.stats.totalRepairs || 0}</div>
                  <div className="text-sm text-gray-600">การซ่อม</div>
                </div>
              </div>
            </div>

            {/* Recent Activities */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">📝 กิจกรรมล่าสุด</h3>
              {loadingActivities ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : recentActivities.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-2">📭</div>
                  <p>ยังไม่มีกิจกรรม</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentActivities.map((activity) => (
                    <div
                      key={activity.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">{getActivityIcon(activity.activityType)}</span>
                        <div>
                          <div className="font-medium text-gray-800">
                            {getActivityLabel(activity.activityType)}
                          </div>
                          <div className="text-sm text-gray-600">
                            {activity.timestamp?.toDate?.()?.toLocaleString('th-TH') || 'ไม่ทราบเวลา'}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        {activity.points > 0 && (
                          <div className="text-sm font-medium text-blue-600">
                            +{activity.points} Points
                          </div>
                        )}
                        {activity.xp > 0 && (
                          <div className="text-sm font-medium text-green-600">
                            +{activity.xp} XP
                          </div>
                        )}
                        {activity.coins > 0 && (
                          <div className="text-sm font-medium text-yellow-600">
                            +{activity.coins} Coins
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'badges' && (
          <BadgeDisplay badges={badges} stats={stats} />
        )}

        {activeTab === 'achievements' && (
          <AchievementDisplay achievements={achievements} stats={stats} />
        )}

        {activeTab === 'challenges' && (
          <ChallengeDisplay userId={currentUser?.uid} teamId={currentTeam?.id} />
        )}

        {activeTab === 'leaderboard' && (
          <LeaderboardDisplay userId={currentUser?.uid} teamId={currentTeam?.id} />
        )}
      </div>
      <NurseBottomNavigation />

      {/* Level Up Modal */}
      {levelUpInfo && (
        <LevelUpModal
          isOpen={!!levelUpInfo}
          onClose={clearLevelUpInfo}
          oldLevel={levelUpInfo.oldLevel}
          newLevel={levelUpInfo.newLevel}
          rewards={levelUpInfo.rewards}
        />
      )}
    </div>
  );
};

export default GamificationDashboardPage;

